// src/pages/shop/ShopSettings.jsx
// src/pages/shop/ShopSettings.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shop as shopApi, upload as uploadApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useToast } from '../../hooks/useToast';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import LanguageSelect from '../../components/LanguageSelect';
import { setMyShop } from '../../hooks/useMyShop';
import { MODULES, isModuleOn } from '../../config/modules';
import { useSupport } from '../../hooks/useSupport';
import { SHOP_NAV, useShopNav } from './ShopHome';
import { COUNTRIES_SORTED, getCountry } from '../../data/countries';
import { CURRENCIES } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import PasswordInput from '../../components/PasswordInput';
import ScanButton from '../../components/ScanButton';
import { QRCodeSVG } from 'qrcode.react';
import { parsePayQr, payQrWithAmount } from '../../utils/elqr';
import DeleteAccountSheet, { DeleteAccountLink } from '../../components/DeleteAccountSheet';

export default function ShopSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, isOwner, isAdmin, isEmployee } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { show, ToastView } = useToast();
  const navItems = useShopNav();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMain, setSavingMain] = useState(false);
  const [savingExtras, setSavingExtras] = useState(false);
  const [savingLoyalty, setSavingLoyalty] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [section, setSection] = useState(null);

  const nameRef = useRef();
  const descRef = useRef();
  const pointsPerSomRef = useRef();
  const minRedeemRef = useRef();
  const whatsappRef = useRef();
  const deliveryInfoRef = useRef();
  const fileRef = useRef();

  const [country, setCountry] = useState('KG');
  const [currency, setCurrency] = useState('KGS');
  const [currencyAuto, setCurrencyAuto] = useState(true); // подставлять валюту по стране автоматически
  const [cartEnabled, setCartEnabled] = useState(false);
  const [inCatalog, setInCatalog] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [wsDm, setWsDm] = useState(false);
  const [savingWsDm, setSavingWsDm] = useState(false);
  const [employeeAuthMode, setEmployeeAuthMode] = useState('password');
  const [shiftsEnabled, setShiftsEnabled] = useState(false);
  const [savingEmployeeSettings, setSavingEmployeeSettings] = useState(false);
  const [discountsEnabled, setDiscountsEnabled] = useState(true);
  const [savingDiscounts, setSavingDiscounts] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const s = await shopApi.my();
      setShop(s);
      setPhoto(s.photo || null);
      setCountry(s.country || 'KG');
      const savedCurrency = s.currency || getCountry(s.country || 'KG').currency || 'KGS';
      setCurrency(savedCurrency);
      // Если валюта совпадает с дефолтной для страны — режим "авто".
      // Если кастомная — показать что переопределена.
      const defaultForCountry = getCountry(s.country || 'KG').currency;
      setCurrencyAuto(savedCurrency === defaultForCountry);
      setCartEnabled(!!s.cartEnabled);
      setInCatalog(!!s.inCatalog);
      setDeliveryEnabled(!!s.deliveryEnabled);
      setLoyaltyEnabled(s.loyaltyEnabled === true); // по умолчанию выключено
      setWsDm(s.wholesaleByDataMatrix === true);
      setEmployeeAuthMode(s.employeeAuthMode || 'password');
      setShiftsEnabled(!!s.shiftsEnabled);
      setDiscountsEnabled(s.discountsEnabled !== false);
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setLoading(false);
  };

  const pickPhoto = () => fileRef.current?.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      const { urls } = await uploadApi.photos(files);
      if (urls?.length) {
        setPhoto(urls[0]);
        const updated = await shopApi.update({ photo: urls[0] });
        setShop(updated); setMyShop(updated);
        show(t('settings.photoUpdated'));
      }
    } catch (err) { show(errorMessage(err, t), 'error'); }
    setUploadingPhoto(false);
    e.target.value = '';
  };

  const removePhoto = async () => {
    if (!window.confirm(t('settings.removePhoto'))) return;
    try {
      const updated = await shopApi.update({ photo: null });
      setShop(updated); setMyShop(updated);
      setPhoto(null);
      show(t('settings.photoRemoved'));
    } catch (err) { show(errorMessage(err, t), 'error'); }
  };

  const saveMain = async () => {
    setSavingMain(true);
    try {
      const data = {
        name: nameRef.current?.value?.trim(),
        description: descRef.current?.value?.trim(),
      };
      if (!data.name) { setSavingMain(false); return show(t('settings.errorNameRequired'), 'error'); }
      const updated = await shopApi.update(data);
      setShop(updated); setMyShop(updated);
      show(t('settings.saved'));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setSavingMain(false);
  };

  const [costMode, setCostMode] = useState(shop?.costPriceMode || 'average');
  const [savingCost, setSavingCost] = useState(false);
  const { contacts } = useSupport();

  const saveCostMode = async (val) => {
    const prev = costMode;
    setCostMode(val);
    setSavingCost(true);
    try {
      const updated = await shopApi.update({ costPriceMode: val });
      setShop(updated); setMyShop(updated);
    } catch (e) { setCostMode(prev); show(errorMessage(e, t), 'error'); }
    setSavingCost(false);
  };

  const saveWsDm = async (val) => {
    setWsDm(val);
    setSavingWsDm(true);
    try {
      const updated = await shopApi.update({ wholesaleByDataMatrix: val });
      setShop(updated); setMyShop(updated);
    } catch (e) { setWsDm(!val); show(errorMessage(e, t), 'error'); }
    setSavingWsDm(false);
  };

  const saveLoyalty = async () => {
    setSavingLoyalty(true);
    try {
      const data = { loyaltyEnabled };
      if (loyaltyEnabled) {
        data.pointsPerSom = parseInt(pointsPerSomRef.current?.value || '1') || 1;
        data.minPointsToRedeem = parseInt(minRedeemRef.current?.value || '500') || 500;
      }
      const updated = await shopApi.update(data);
      setShop(updated); setMyShop(updated);
      show(t('settings.saved'));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setSavingLoyalty(false);
  };

  const saveExtras = async () => {
    setSavingExtras(true);
    try {
      const data = {
        country,
        currency,
        cartEnabled,
        inCatalog,
        whatsappNumber: whatsappRef.current?.value?.trim() || null,
        deliveryEnabled,
        deliveryInfo: deliveryInfoRef.current?.value?.trim() || null,
      };
      if (cartEnabled && !data.whatsappNumber) {
        setSavingExtras(false);
        return show(t('settings.errorWhatsappRequired'), 'error');
      }
      const updated = await shopApi.updateExtras(data);
      setShop(updated); setMyShop(updated);
      show(t('settings.saved'));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setSavingExtras(false);
  };

  const saveEmployeeSettings = async () => {
    setSavingEmployeeSettings(true);
    try {
      const updated = await shopApi.updateExtras({
        employeeAuthMode,
        shiftsEnabled,
      });
      setShop(updated); setMyShop(updated);
      show(t('settings.saved'));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setSavingEmployeeSettings(false);
  };

  const saveDiscounts = async () => {
    setSavingDiscounts(true);
    try {
      const updated = await shopApi.updateExtras({
        discountsEnabled,
      });
      setShop(updated); setMyShop(updated);
      show(t('settings.saved'));
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setSavingDiscounts(false);
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  const SECTIONS = [
    { key: 'profile',   icon: 'store',    label: t('settings.tabProfile'),   show: isOwner || isAdmin },
    { key: 'shop',      icon: 'cart',     label: t('settings.tabShop'),      show: isOwner || isAdmin },
    { key: 'interface', icon: 'settings', label: t('settings.tabInterface'), show: true },
    { key: 'access',    icon: 'users',    label: t('settings.tabAccess'),    show: isOwner || isAdmin },
    { key: 'system',    icon: 'lock',     label: t('settings.tabSystem'),    show: isOwner },
  ];
  const activeLabel = SECTIONS.find(s => s.key === section)?.label || '';

  return (
    <div className="page">
      {ToastView}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {section && (
          <button className="back-btn" onClick={() => setSection(null)}>
            <Icon name="chevronLeft" size={18} />
          </button>
        )}
        <div>
          <p className="page-subtitle">{section ? t('settings.title') : t('settings.subtitle')}</p>
          <h1 className="page-title">{section ? activeLabel : t('settings.title')}</h1>
        </div>
      </div>

      {!section && (
        <div style={{ padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {SECTIONS.filter(s => s.show).map(s => (
              <button key={s.key} onClick={() => setSection(s.key)} className="action-tile">
                <div className="ico"><Icon name={s.icon} size={22} /></div>
                <p>{s.label}</p>
              </button>
            ))}
          </div>
          <button onClick={() => { if (window.confirm(t('settings.logoutConfirm'))) { logout(); navigate('/'); } }}
            className="btn btn-ghost" style={{ color: 'var(--danger)', width: '100%' }}>
            <Icon name="logout" size={16} />
            {t('common.logout')}
          </button>
          {isOwner && <DeleteAccountLink onClick={() => setDeleteOpen(true)} />}
          <div style={{ height: 20 }} />
          {deleteOpen && (
            <DeleteAccountSheet
              isShop
              onClose={() => setDeleteOpen(false)}
              onDeleted={() => { logout(); navigate('/'); }}
            />
          )}
        </div>
      )}

      {section && (
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {section === 'profile' && (isOwner || isAdmin) && <>
        {/* Фото магазина */}
        <div className="card">
          <SectionTitle>{t("settings.shopPhoto")}</SectionTitle>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
          <div onClick={!uploadingPhoto ? pickPhoto : undefined} style={{
            width: '100%', aspectRatio: '16/9',
            background: 'var(--bg-subtle)', border: '2px dashed var(--border)',
            borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: uploadingPhoto ? 'default' : 'pointer',
            overflow: 'hidden',
          }}>
            {uploadingPhoto ? (
              <div className="spinner" />
            ) : photo ? (
              <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--fg-faint)' }}>
                <Icon name="image" size={28} stroke={1.5} style={{ margin: '0 auto 6px' }} />
                <div style={{ fontSize: 13 }}>{t("settings.tapToSelect")}</div>
              </div>
            )}
          </div>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8 }}>
            {t("settings.shopPhotoHint")}
          </p>
          {photo && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-secondary btn-sm" onClick={pickPhoto} disabled={uploadingPhoto} style={{ flex: 1 }}>
                {t('settings.replace')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={removePhoto} disabled={uploadingPhoto} style={{ flex: 1, color: 'var(--danger)' }}>
                {t('settings.remove')}
              </button>
            </div>
          )}
        </div>

        {/* Основные данные */}
        <div className="card">
          <SectionTitle>{t("settings.main")}</SectionTitle>
          <div className="input-group">
            <label>{t("products.name")}</label>
            <input ref={nameRef} className="input" defaultValue={shop?.name || ''} />
          </div>
          <div className="input-group">
            <label>{t("products.description")}</label>
            <textarea ref={descRef} className="textarea" defaultValue={shop?.description || ''} />
          </div>

          <button className="btn btn-primary" onClick={saveMain} disabled={savingMain}>
            {savingMain ? t('common.saving') : t('settings.saveMain')}
          </button>
        </div>
        </>}

        {section === 'shop' && (isOwner || isAdmin) && <>
        {/* Программа баллов */}
        <div className="card">
          <SectionTitle>{t('settings.loyaltyTitle')}</SectionTitle>
          <Toggle
            label={t("settings.loyaltyToggle")}
            desc={loyaltyEnabled ? t("settings.loyaltyToggleOn") : t("settings.loyaltyToggleOff")}
            value={loyaltyEnabled}
            onChange={setLoyaltyEnabled}
          />
          {loyaltyEnabled && (
            <div className="grid-2" style={{ marginTop: 14 }}>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label>{t("settings.pointsPerSom")}</label>
                <input ref={pointsPerSomRef} className="input" type="number" inputMode="numeric" defaultValue={shop?.pointsPerSom || 1} />
              </div>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label>{t("settings.minRedeem")}</label>
                <input ref={minRedeemRef} className="input" type="number" inputMode="numeric" defaultValue={shop?.minPointsToRedeem || 500} />
              </div>
            </div>
          )}
          <button className="btn btn-primary" onClick={saveLoyalty} disabled={savingLoyalty} style={{ marginTop: 14 }}>
            {savingLoyalty ? t('common.saving') : t('settings.saveButton')}
          </button>
        </div>

        {/* QR банков для «Безнал» — только владелец (PATCH /shop/my) */}
        {isOwner && <PayQrSection t={t} shop={shop} onUpdated={(u) => { setShop(u); setMyShop(u); }} show={show} />}

        {/* Страна и валюта */}
        <div className="card">
          <SectionTitle>
            <Icon name="globe" size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {t("settings.country")}
          </SectionTitle>
          <div className="input-group">
            <label>{t("settings.country")}</label>
            <select
              className="select"
              value={country}
              onChange={e => {
                const newCountry = e.target.value;
                setCountry(newCountry);
                // Если режим автоматический — обновляем и валюту
                if (currencyAuto) {
                  const cur = getCountry(newCountry).currency || 'KGS';
                  setCurrency(cur);
                }
              }}
            >
              {COUNTRIES_SORTED.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
              {t("settings.countryHint")}
            </p>
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}>
            <label>
              {t("settings.currency")}
              {currencyAuto && (
                <span style={{ fontSize: 11, color: 'var(--fg-faint)', marginLeft: 6, fontWeight: 400 }}>
                  ({t('settings.currencyAuto')})
                </span>
              )}
            </label>
            <select
              className="select"
              value={currency}
              onChange={e => {
                const newCurrency = e.target.value;
                setCurrency(newCurrency);
                // Если выбрали что-то отличное от дефолта страны — выключаем "авто"
                const defaultForCountry = getCountry(country).currency;
                setCurrencyAuto(newCurrency === defaultForCountry);
              }}
            >
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.symbol} {c.code} — {c.name}
                </option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
              {t("settings.currencyHint")}
            </p>
          </div>
        </div>

        {/* Корзина */}
        <div className="card">
          <SectionTitle>
            <Icon name="cart" size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {t("settings.cart")}
          </SectionTitle>
          <Toggle
            label={t("settings.cartToggle")}
            desc={t("settings.cartToggleHint")}
            value={cartEnabled}
            onChange={setCartEnabled}
          />
          {cartEnabled && (
            <div className="input-group" style={{ marginTop: 14 }}>
              <label>{t("settings.whatsappNumber")}</label>
              <input ref={whatsappRef} className="input" type="tel" placeholder="996555123456"
                defaultValue={shop?.whatsappNumber || ''} />
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
                {t("settings.whatsappHint")}
              </p>
            </div>
          )}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <Toggle
              label={t("settings.catalogToggle")}
              desc={t("settings.catalogToggleHint")}
              value={inCatalog}
              onChange={setInCatalog}
            />
          </div>
        </div>

        {/* Доставка */}
        <div className="card">
          <SectionTitle>
            <Icon name="truck" size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {t("settings.delivery")}
          </SectionTitle>
          <Toggle
            label={t("settings.deliveryToggle")}
            desc={t("settings.deliveryToggleHint")}
            value={deliveryEnabled}
            onChange={setDeliveryEnabled}
          />
          {deliveryEnabled && (
            <div className="input-group" style={{ marginTop: 14 }}>
              <label>{t("settings.deliveryInfo")}</label>
              <textarea ref={deliveryInfoRef} className="textarea"
                placeholder={t("settings.deliveryInfoPlaceholder")}
                defaultValue={shop?.deliveryInfo || ''} />
            </div>
          )}
        </div>

        <button className="btn btn-primary" onClick={saveExtras} disabled={savingExtras}>
          {savingExtras ? t('common.saving') : t('settings.saveExtras')}
        </button>
        </>}

        {section === 'interface' && <>
        {/* Тема */}
        <div className="card">
          <SectionTitle>{t('common.appearance')}</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => setTheme('light')} style={themeBtn(theme === 'light')}>
              <Icon name="sun" size={16} />
              {t('common.themeLight')}
            </button>
            <button onClick={() => setTheme('dark')} style={themeBtn(theme === 'dark')}>
              <Icon name="moon" size={16} />
              {t('common.themeDark')}
            </button>
          </div>
        </div>

        {/* Язык */}
        <div className="card">
          <SectionTitle>{t('common.language')}</SectionTitle>
          <LanguageSelect />
        </div>
        </>}

        {section === 'access' && <>
        {(isOwner || isAdmin) && (
          <div className="card">
            <SectionTitle>{t('employees.settingsTitle')}</SectionTitle>

            <div className="input-group" style={{ marginTop: 12 }}>
              <label>{t('employees.authModeLabel')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { code: 'password', label: t('employees.authModePassword') },
                  { code: 'pin',      label: t('employees.authModePin') },
                ].map(m => (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => setEmployeeAuthMode(m.code)}
                    style={{
                      flex: 1, padding: '10px',
                      borderRadius: 'var(--r-sm)',
                      border: '1px solid',
                      borderColor: employeeAuthMode === m.code ? 'var(--brand-primary)' : 'var(--border)',
                      background: employeeAuthMode === m.code ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
                      color: employeeAuthMode === m.code ? 'var(--brand-primary)' : 'var(--fg-muted)',
                      fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)',
                    }}
                  >{m.label}</button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
                {employeeAuthMode === 'pin' ? t('employees.authModePinHint') : t('employees.authModePasswordHint')}
              </p>
            </div>

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              cursor: 'pointer', marginTop: 12,
            }}>
              <input
                type="checkbox"
                checked={shiftsEnabled}
                onChange={e => setShiftsEnabled(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  {t('employees.shiftsEnabledLabel')}
                </p>
                <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.5 }}>
                  {t('employees.shiftsEnabledHint')}
                </p>
              </div>
            </label>

            <button
              onClick={saveEmployeeSettings}
              disabled={savingEmployeeSettings}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: 12 }}
            >
              {savingEmployeeSettings ? '…' : t('settings.saveButton')}
            </button>
          </div>
        )}

        {(isOwner || isAdmin) && (
          <div className="card">
            <SectionTitle>{t('discounts.sectionTitle')}</SectionTitle>

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              cursor: 'pointer', marginTop: 12,
            }}>
              <input
                type="checkbox"
                checked={discountsEnabled}
                onChange={e => setDiscountsEnabled(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  {t('discounts.enabledLabel')}
                </p>
                <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.5 }}>
                  {t('discounts.enabledHint')}
                </p>
              </div>
            </label>

            {discountsEnabled && (
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 12, lineHeight: 1.5 }}>
                {t('discounts.perEmployeeHint')}
              </p>
            )}

            <button
              onClick={saveDiscounts}
              disabled={savingDiscounts}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: 12 }}
            >
              {savingDiscounts ? '…' : t('settings.saveButton')}
            </button>
          </div>
        )}

        {(isOwner || !isEmployee) && isModuleOn(shop, 'employees') && (
          <div className="card">
            {isOwner && (
              <button onClick={() => navigate('/employees')} style={menuBtn}>
                <Icon name="users" size={18} color="var(--fg-muted)" />
                <span style={{ flex: 1, textAlign: 'left' }}>{t('employees.sectionTitle')}</span>
                <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
              </button>
            )}
          </div>
        )}

        {(isOwner || isAdmin) && isModuleOn(shop, 'counterparties') && (
          <div className="card">
            <button onClick={() => navigate('/counterparties')} style={menuBtn}>
              <Icon name="truck" size={18} color="var(--fg-muted)" />
              <span style={{ flex: 1, textAlign: 'left' }}>{t('counterparties.sectionTitle')}</span>
              <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
            </button>
          </div>
        )}
        </>}

        {section === 'system' && <>
        {/* Модули — только владельцу */}
        {isOwner && <ModulesSection t={t} shop={shop} onUpdated={(u) => { setShop(u); setMyShop(u); }} show={show} />}

        {/* Отгрузка оптовику по DataMatrix — если включён модуль опта */}
        {isOwner && isModuleOn(shop, 'wholesale') && (
          <div className="card">
            <SectionTitle>{t('settings.wholesaleDmTitle')}</SectionTitle>
            <Toggle
              label={t('settings.wholesaleDmToggle')}
              desc={wsDm ? t('settings.wholesaleDmOn') : t('settings.wholesaleDmOff')}
              value={wsDm}
              onChange={saveWsDm}
            />
          </div>
        )}

        {/* Себестоимость при приходе */}
        {isOwner && isModuleOn(shop, 'purchases') && (
          <div className="card">
            <SectionTitle>{t('settings.costPriceTitle')}</SectionTitle>
            <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>
              {t('settings.costPriceHint')}
            </p>
            {['average', 'last', 'keep'].map(mode => (
              <button
                key={mode}
                onClick={() => saveCostMode(mode)}
                disabled={savingCost}
                style={{
                  width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '11px 12px', marginBottom: 6,
                  background: costMode === mode ? 'var(--bg-subtle)' : 'transparent',
                  border: '1px solid ' + (costMode === mode ? 'var(--brand-primary)' : 'var(--border)'),
                  borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                  border: '2px solid ' + (costMode === mode ? 'var(--brand-primary)' : 'var(--border)'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {costMode === mode && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-primary)' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t(`settings.costMode_${mode}`)}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.4 }}>
                    {t(`settings.costMode_${mode}_desc`)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}


        {/* PIN на удаление — только владельцу */}
        {isOwner && <DeletePinSection t={t} show={show} />}
        </>}

        {/* Связь с разработчиком — ВНЕ вкладок, виден всем.
            Раньше лежал во вкладке «Система», а она только для владельца —
            то есть кассир, который и ловит баги, написать не мог. */}
        <div className="card">
          <SectionTitle>{t('support.title')}</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>
            {t('support.hint')}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            {contacts.whatsapp && (
              <a
                className="btn btn-secondary"
                href={contacts.whatsapp}
                target="_blank"
                rel="noreferrer"
                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Icon name="whatsapp" size={16} /> WhatsApp
              </a>
            )}
            {contacts.telegram && (
              <a
                className="btn btn-secondary"
                href={contacts.telegram}
                target="_blank"
                rel="noreferrer"
                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Icon name="share" size={16} /> Telegram
              </a>
            )}
          </div>
        </div>

        <div style={{ height: 20 }} />
      </div>
      )}

      <BottomNav items={navItems} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{
      fontSize: 11, fontWeight: 700,
      color: 'var(--fg-muted)',
      letterSpacing: '0.08em', marginBottom: 12,
      fontFamily: 'var(--font-display)',
    }}>{children}</h3>
  );
}

function Toggle({ label, desc, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>{label}</p>
        {desc && <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 2 }}>{desc}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
          width: 48, height: 28, borderRadius: 999,
          background: value ? 'var(--brand-primary)' : 'var(--bg-subtle)',
          border: 'none', position: 'relative',
          cursor: 'pointer', transition: 'background 0.2s',
          flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: value ? 23 : 3,
          width: 22, height: 22, borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s',
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );
}

const themeBtn = (active) => ({
  padding: 12, borderRadius: 'var(--r-sm)',
  border: '1px solid', borderColor: active ? 'var(--brand-primary)' : 'var(--border)',
  background: active ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
  color: active ? 'var(--brand-primary)' : 'var(--fg-muted)',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'var(--font-display)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
});

const menuBtn = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 4px', background: 'none', border: 'none',
  fontSize: 14, fontWeight: 600, color: 'var(--fg)',
  cursor: 'pointer', fontFamily: 'var(--font-display)',
};

// ─── QR банков для «Безнал» (ELQR: MBank, Optima, O!Bank…) ──
// Продавец добавляет СВОЙ пустой QR из банка (скриншот или камерой).
// На кассе после «Безнал» выбирается банк — показывается этот QR с вписанной суммой.
function PayQrSection({ t, shop, onUpdated, show }) {
  const list = Array.isArray(shop?.payQrs) ? shop.payQrs : [];
  const [draft, setDraft] = useState(null);   // разобранный QR, ждёт названия банка
  const [test, setTest] = useState(null);     // QR для проверки на 1 сом
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const save = async (next) => {
    setSaving(true);
    try {
      const updated = await shopApi.update({ payQrs: next });
      onUpdated(updated);
      return true;
    } catch (e) { show(errorMessage(e, t), 'error'); return false; }
    finally { setSaving(false); }
  };

  const accept = (raw) => {
    const info = parsePayQr(raw);
    if (!info) return show(t('payQr.notPayQr'), 'error');
    if (list.some(q => q.raw === info.raw)) return show(t('payQr.duplicate'), 'error');
    setDraft(info);
  };

  // Скриншот из галереи → распознаём QR на картинке
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const id = 'alsatuu-payqr-decode';
    let el = document.getElementById(id);
    if (!el) { el = document.createElement('div'); el.id = id; el.style.display = 'none'; document.body.appendChild(el); }
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const reader = new Html5Qrcode(id);
      const text = await reader.scanFile(file, false);
      reader.clear();
      accept(text);
    } catch (err) { show(t('payQr.notFoundOnImage'), 'error'); }
  };

  const addDraft = async () => {
    const bank = (draft.bank || '').trim() || t('payQr.bank');
    const item = { id: Date.now().toString(36), bank, raw: draft.raw, recipient: draft.recipient };
    if (await save([...list, item])) { setDraft(null); show(t('payQr.added')); }
  };

  const remove = async (q) => {
    if (!window.confirm(t('payQr.removeConfirm', { bank: q.bank }))) return;
    await save(list.filter(x => x !== q));
  };

  return (
    <div className="card">
      <SectionTitle>{t('payQr.title')}</SectionTitle>
      <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>{t('payQr.hint')}</p>

      {list.map(q => {
        const info = parsePayQr(q.raw);
        return (
          <div key={q.id || q.raw} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 700 }}>{q.bank}</p>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{[q.recipient, info?.phone && '+' + info.phone].filter(Boolean).join(' · ')}</p>
              {!info?.check && <p style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>{t('payQr.noAmountSupport')}</p>}
            </div>
            <button className="btn btn-secondary" style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }} onClick={() => setTest(q)}>
              {t('payQr.test')}
            </button>
            <button onClick={() => remove(q)} disabled={saving} aria-label={t('common.delete')}
              style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 6 }}>
              <Icon name="trash" size={16} />
            </button>
          </div>
        );
      })}

      {draft ? (
        <div style={{ padding: 12, border: '1px solid var(--brand-primary)', borderRadius: 'var(--r-sm)', marginTop: 6 }}>
          <p style={{ fontSize: 13, marginBottom: 8 }}>
            {t('payQr.found')}: <b>{[draft.recipient, draft.phone && '+' + draft.phone].filter(Boolean).join(' · ') || '—'}</b>
          </p>
          {draft.hasAmount && <p style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>{t('payQr.hasAmountWarn')}</p>}
          {!draft.check && <p style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>{t('payQr.noAmountSupport')}</p>}
          <div className="input-group" style={{ marginBottom: 10 }}>
            <label>{t('payQr.bankName')}</label>
            <input className="input" value={draft.bank} placeholder="MBank" onChange={e => setDraft({ ...draft, bank: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setDraft(null)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={addDraft} disabled={saving}>{saving ? t('common.saving') : t('payQr.add')}</button>
          </div>
        </div>
      ) : list.length < 10 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Icon name="image" size={16} /> {t('payQr.fromImage')}
          </button>
          <ScanButton raw variant="button" label={t('payQr.scan')} onScan={accept} style={{ flex: 1, justifyContent: 'center' }} />
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
        </div>
      )}

      {test && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setTest(null)}>
          <div className="modal-sheet" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <p style={{ flex: 1, fontSize: 16, fontWeight: 800, textAlign: 'left' }}>{test.bank}</p>
              <button className="modal-close" onClick={() => setTest(null)}><Icon name="x" size={16} /></button>
            </div>
            <div style={{ display: 'inline-block', background: '#fff', padding: 12, borderRadius: 'var(--r-md)', lineHeight: 0 }}>
              <QRCodeSVG value={payQrWithAmount(test.raw, 1) || test.raw} size={240} level="M" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 12, lineHeight: 1.5 }}>{t('payQr.testHint')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Блок «Модули» — вкл/выкл разделов интерфейса (только владелец) ──
function ModulesSection({ t, shop, onUpdated, show }) {
  const [saving, setSaving] = useState(null); // ключ модуля в процессе сохранения

  const toggle = async (key, next) => {
    setSaving(key);
    try {
      const cfg = { ...((shop && shop.moduleConfig) || {}), [key]: next };
      const updated = await shopApi.updateModules(cfg);
      onUpdated(updated);
    } catch (e) {
      show(errorMessage(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="card">
      <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('settings.modulesTitle')}</p>
      <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginBottom: 12 }}>{t('settings.modulesHint')}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {MODULES.map(m => {
          const depBlocked = (m.requires || []).some(dep => !isModuleOn(shop, dep));
          const value = depBlocked ? false : isModuleOn(shop, m.key);
          const depName = depBlocked ? t(MODULES.find(x => x.key === m.requires[0])?.labelKey || '') : '';
          return (
            <div key={m.key} style={{ opacity: depBlocked ? 0.5 : 1 }}>
              <Toggle
                label={t(m.labelKey)}
                desc={depBlocked ? t('settings.moduleNeeds', { dep: depName }) : undefined}
                value={value}
                onChange={(next) => { if (!depBlocked && saving !== m.key) toggle(m.key, next); }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Блок управления PIN на удаление ─────────────────────────────────
function DeletePinSection({ t, show }) {
  const [pinSet, setPinSet] = useState(null);
  const [mode, setMode] = useState(null); // null | 'set' | 'change' | 'remove'
  const [password, setPassword] = useState('');   // пароль аккаунта — им подтверждаем смену/снятие PIN
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    shopApi.pinStatus()
      .then(r => setPinSet(!!r.pinSet))
      .catch(() => setPinSet(false));
  }, []);

  const reset = () => {
    setMode(null);
    setPassword('');
    setNewPin('');
    setConfirmPin('');
    setError('');
    setBusy(false);
  };

  const validate4 = (s) => /^\d{4}$/.test(s);

  const onSet = async () => {
    setError('');
    if (!validate4(newPin)) { setError(t('deletePin.errPinFormat')); return; }
    if (newPin !== confirmPin) { setError(t('deletePin.errPinMismatch')); return; }
    if (pinSet && !password) { setError(t('deletePin.errPasswordRequired')); return; }
    setBusy(true);
    try {
      await shopApi.pinSet({ pin: newPin, password: pinSet ? password : undefined });
      setPinSet(true);
      show(t('deletePin.pinUpdated'));
      reset();
    } catch (e) {
      setError(pinErr(e, t));
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setError('');
    if (!password) { setError(t('deletePin.errPasswordRequired')); return; }
    setBusy(true);
    try {
      await shopApi.pinRemove({ password });
      setPinSet(false);
      show(t('deletePin.pinRemoved'));
      reset();
    } catch (e) {
      setError(pinErr(e, t));
      setBusy(false);
    }
  };

  if (pinSet === null) {
    return null;
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ padding: '4px 4px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Icon name="lock" size={18} color="var(--fg-muted)" />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>
            {t('deletePin.sectionTitle')}
          </p>
          <p style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
            {pinSet ? t('deletePin.pinIsSet') : t('deletePin.pinNotSet')}
          </p>
        </div>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: pinSet ? 'var(--success)' : 'var(--fg-faint)',
        }} />
      </div>
      <p style={{ fontSize: 11, color: 'var(--fg-faint)', padding: '0 4px 4px', lineHeight: 1.4 }}>
        {t('deletePin.sectionHint')}
      </p>

      {mode === null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0 0' }}>
          <button onClick={() => setMode('set')} className="btn btn-secondary btn-sm" style={{ width: '100%' }}>
            {pinSet ? t('deletePin.changePin') : t('deletePin.setPin')}
          </button>
          {pinSet && (
            <button
              onClick={() => setMode('remove')}
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', color: 'var(--danger)' }}
            >
              {t('deletePin.removePin')}
            </button>
          )}
        </div>
      )}

      {(mode === 'set' || mode === 'change') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 0' }}>
          {pinSet && (
            <div>
              <label style={miniLabel}>{t('deletePin.accountPassword')}</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••"
                autoCapitalize="none"
                autoCorrect="off"
              />
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4, lineHeight: 1.4 }}>
                {t('deletePin.accountPasswordHint')}
              </p>
            </div>
          )}
          <div>
            <label style={miniLabel}>{t('deletePin.newPin')}</label>
            <PasswordInput
              inputMode="numeric"
              maxLength={4}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              className="input"
              placeholder="••••"
            />
          </div>
          <div>
            <label style={miniLabel}>{t('deletePin.confirmNewPin')}</label>
            <PasswordInput
              inputMode="numeric"
              maxLength={4}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              className="input"
              placeholder="••••"
            />
          </div>
          {error && <p style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={reset} disabled={busy} className="btn btn-ghost" style={{ flex: 1 }}>
              {t('common.cancel')}
            </button>
            <button onClick={onSet} disabled={busy} className="btn btn-primary" style={{ flex: 1 }}>
              {busy ? '…' : t('common.save')}
            </button>
          </div>
        </div>
      )}

      {mode === 'remove' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 0' }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            {t('deletePin.confirmRemove')}
          </p>
          <div>
            <label style={miniLabel}>{t('deletePin.accountPassword')}</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>
          {error && <p style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={reset} disabled={busy} className="btn btn-ghost" style={{ flex: 1 }}>
              {t('common.cancel')}
            </button>
            <button onClick={onRemove} disabled={busy} className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }}>
              {busy ? '…' : t('deletePin.removePin')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const miniLabel = {
  fontSize: 10, fontWeight: 700, color: 'var(--fg-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 4,
};

// Разбор ошибок PIN-эндпоинтов в человеческий текст
function pinErr(e, t) {
  const m = e?.message;
  if (m === 'wrong_password') return t('deletePin.errWrongPassword');
  if (m === 'too_many_attempts') return t('deletePin.errTooMany');
  if (m === 'invalid_pin') return t('deletePin.errPinFormat');
  return m || t('common.error');
}
